/**
 * PreferencesPage Component
 * Consolidated preferences management screen
 *
 * Features:
 * - Dietary restrictions and preferences
 * - Kitchen appliances
 * - Default session preferences
 * - "Remember settings" toggle
 */

import { useState, useEffect } from 'react'
import { useUserId, usePreferences, DIETARY_PREFERENCES, AVAILABLE_APPLIANCES, UserPreferences } from '@talktoast/shared'
import { Button, Card } from '@talktoast/ui'
import { ICONS, getIconSize, getIconColor } from '@talktoast/shared'
import { showSuccess, showError } from '../utils/toast'

// Available cuisines with emojis
const CUISINES = [
  { name: 'Italian', emoji: '🇮🇹' },
  { name: 'Mexican', emoji: '🇲🇽' },
  { name: 'Chinese', emoji: '🇨🇳' },
  { name: 'Japanese', emoji: '🇯🇵' },
  { name: 'Thai', emoji: '🇹🇭' },
  { name: 'Indian', emoji: '🇮🇳' },
  { name: 'Korean', emoji: '🇰🇷' },
  { name: 'Mediterranean', emoji: '🌊' },
  { name: 'French', emoji: '🇫🇷' },
  { name: 'American', emoji: '🇺🇸' },
  { name: 'Spanish', emoji: '🇪🇸' },
  { name: 'Middle Eastern', emoji: '🕌' },
  { name: 'Vietnamese', emoji: '🇻🇳' },
  { name: 'Greek', emoji: '🇬🇷' },
  { name: 'Cajun/Creole', emoji: '🎺' },
  { name: 'British', emoji: '🇬🇧' },
  { name: 'Caribbean', emoji: '🏝️' },
  { name: 'Brazilian', emoji: '🇧🇷' },
]

export interface PreferencesPageProps {
  /**
   * Callback to navigate back
   */
  onBack: () => void
}

export default function PreferencesPage({ onBack }: PreferencesPageProps) {
  const userId = useUserId()
  const { preferences, loading, updatePersistentPreferences, updateSessionPreferences } = usePreferences()

  // Section expansion state
  const [expandedSection, setExpandedSection] = useState<string | null>(null)

  // Local state for editing
  const [editedPrefs, setEditedPrefs] = useState<UserPreferences | null>(preferences)

  // Custom restriction input state
  const [customRestriction, setCustomRestriction] = useState('')

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [])

  // Sync local state when preferences prop changes (after save)
  useEffect(() => {
    setEditedPrefs(preferences)
  }, [preferences])

  const handleToggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section)
  }

  const handleSave = async () => {
    try {
      if (!editedPrefs) return

      // Save persistent preferences
      if (editedPrefs.persistent) {
        await updatePersistentPreferences(editedPrefs.persistent)
      }

      // Save session preferences
      if (editedPrefs.session) {
        await updateSessionPreferences(editedPrefs.session)
      }

      showSuccess('Preferences saved successfully')
      onBack() // Navigate back after successful save
    } catch (error) {
      console.error('[PreferencesPage] Save failed:', error)
      showError('Failed to save preferences')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-toast-gold mx-auto mb-2"></div>
          <p className="text-sm text-gray-600">Loading preferences...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gray-50 min-h-full">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm">
        <div className="flex items-center justify-between px-4 py-2 h-12">
          <button
            onClick={onBack}
            className="text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            ← Back
          </button>
          <h1 className="text-base font-semibold text-gray-900">Preferences</h1>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
          >
            Save
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="p-4 spacing-page">
        {/* PLANNING PREFERENCES SECTION */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              Planning Preferences
            </h2>
            <div className="flex-1 h-px bg-gray-200"></div>
          </div>

          {/* Remember Settings Toggle */}
          <Card className="p-4 border-2 border-gray-200 mb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-toast-gold-100 flex items-center justify-center flex-shrink-0">
                  <ICONS.navigation.settings className={`${getIconSize('md')} ${getIconColor('primary')}`} />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">
                    Remember My Settings
                  </h3>
                  <p className="text-xs text-gray-500">
                    Auto-fill preferences when planning
                  </p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={editedPrefs?.session?.rememberSettings || false}
                  onChange={(e) => {
                    if (!editedPrefs) return
                    setEditedPrefs({
                      ...editedPrefs,
                      session: {
                        ...editedPrefs.session!,
                        rememberSettings: e.target.checked
                      }
                    })
                  }}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-toast-gold rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-toast-gold"></div>
              </label>
            </div>
          </Card>

          {/* Default Servings Card */}
          <Card className="p-4 border-2 border-gray-200 hover:border-toast-gold transition-all cursor-pointer mb-3">
            <button
              onClick={() => handleToggleSection('servings')}
              className="w-full text-left"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-toast-gold-100 flex items-center justify-center flex-shrink-0">
                    <ICONS.user.users className={`${getIconSize('md')} ${getIconColor('primary')}`} />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">
                      Servings
                    </h3>
                    <p className="text-xs text-gray-500">
                      {editedPrefs?.session?.numberOfPeople || 2} people
                    </p>
                  </div>
                </div>
                <ICONS.navigation.forward className={`${getIconSize('md')} ${getIconColor('muted')} transition-transform ${expandedSection === 'servings' ? 'rotate-90' : ''}`} />
              </div>
            </button>

            {/* Expanded Content */}
            {expandedSection === 'servings' && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      if (!editedPrefs) return
                      const currentValue = editedPrefs.session?.numberOfPeople || 2
                      setEditedPrefs({
                        ...editedPrefs,
                        session: {
                          ...editedPrefs.session!,
                          numberOfPeople: Math.max(1, currentValue - 1)
                        }
                      })
                    }}
                    className="w-10 h-10 rounded-lg border-2 border-gray-200 hover:border-toast-gold flex items-center justify-center text-gray-700 font-semibold"
                  >
                    −
                  </button>
                  <div className="flex-1 text-center">
                    <span className="text-2xl font-bold text-gray-900">
                      {editedPrefs?.session?.numberOfPeople || 2}
                    </span>
                    <p className="text-xs text-gray-500">people</p>
                  </div>
                  <button
                    onClick={() => {
                      if (!editedPrefs) return
                      const currentValue = editedPrefs.session?.numberOfPeople || 2
                      setEditedPrefs({
                        ...editedPrefs,
                        session: {
                          ...editedPrefs.session!,
                          numberOfPeople: Math.min(12, currentValue + 1)
                        }
                      })
                    }}
                    className="w-10 h-10 rounded-lg border-2 border-gray-200 hover:border-toast-gold flex items-center justify-center text-gray-700 font-semibold"
                  >
                    +
                  </button>
                </div>
              </div>
            )}
          </Card>

          {/* Default Meal Types Card */}
          <Card className="p-4 border-2 border-gray-200 hover:border-toast-gold transition-all cursor-pointer">
            <button
              onClick={() => handleToggleSection('meal-types')}
              className="w-full text-left"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-toast-gold-100 flex items-center justify-center flex-shrink-0">
                    <ICONS.food.utensils className={`${getIconSize('md')} ${getIconColor('primary')}`} />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">
                      Meal Types
                    </h3>
                    <p className="text-xs text-gray-500">
                      {editedPrefs?.session?.mealTypes?.filter(mt => mt.enabled).length || 0} meal types
                    </p>
                  </div>
                </div>
                <ICONS.navigation.forward className={`${getIconSize('md')} ${getIconColor('muted')} transition-transform ${expandedSection === 'meal-types' ? 'rotate-90' : ''}`} />
              </div>
            </button>

            {/* Expanded Content */}
            {expandedSection === 'meal-types' && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="space-y-3">
                  {['breakfast', 'lunch', 'dinner', 'snacks'].map((mealType) => {
                    const mealTypeObj = editedPrefs?.session?.mealTypes?.find(mt => mt.type === mealType)
                    const isEnabled = mealTypeObj?.enabled || false
                    const mealTypeCount = editedPrefs?.session?.mealCounts?.[mealType as keyof typeof editedPrefs.session.mealCounts] || 0

                    return (
                      <div key={mealType} className="p-2 rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between mb-2">
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={isEnabled}
                              onChange={(e) => {
                                if (!editedPrefs) return
                                const updatedMealTypes = editedPrefs.session!.mealTypes.map(mt =>
                                  mt.type === mealType ? { ...mt, enabled: e.target.checked } : mt
                                )
                                setEditedPrefs({
                                  ...editedPrefs,
                                  session: {
                                    ...editedPrefs.session!,
                                    mealTypes: updatedMealTypes
                                  }
                                })
                              }}
                              className="w-5 h-5 rounded border-2 border-gray-300 text-toast-gold focus:ring-2 focus:ring-toast-gold"
                            />
                            <span className="text-sm font-medium text-gray-900 capitalize">{mealType}</span>
                          </label>
                        </div>

                        {/* Quantity selector (only shown when checked) */}
                        {isEnabled && (
                          <div className="flex items-center gap-2 ml-8">
                            <button
                              onClick={() => {
                                if (!editedPrefs) return
                                const currentCount = editedPrefs.session!.mealCounts[mealType as keyof typeof editedPrefs.session.mealCounts]
                                setEditedPrefs({
                                  ...editedPrefs,
                                  session: {
                                    ...editedPrefs.session!,
                                    mealCounts: {
                                      ...editedPrefs.session!.mealCounts,
                                      [mealType]: Math.max(1, currentCount - 1)
                                    }
                                  }
                                })
                              }}
                              className="w-8 h-8 rounded border-2 border-gray-200 hover:border-toast-gold flex items-center justify-center text-gray-700 font-semibold text-sm"
                            >
                              −
                            </button>
                            <div className="flex-1 text-center">
                              <span className="text-sm font-bold text-gray-900">{mealTypeCount}</span>
                              <span className="text-xs text-gray-500 ml-1">per week</span>
                            </div>
                            <button
                              onClick={() => {
                                if (!editedPrefs) return
                                const currentCount = editedPrefs.session!.mealCounts[mealType as keyof typeof editedPrefs.session.mealCounts]
                                setEditedPrefs({
                                  ...editedPrefs,
                                  session: {
                                    ...editedPrefs.session!,
                                    mealCounts: {
                                      ...editedPrefs.session!.mealCounts,
                                      [mealType]: Math.min(7, currentCount + 1)
                                    }
                                  }
                                })
                              }}
                              className="w-8 h-8 rounded border-2 border-gray-200 hover:border-toast-gold flex items-center justify-center text-gray-700 font-semibold text-sm"
                            >
                              +
                            </button>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </Card>
        </section>

        {/* DIETARY SECTION */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              Dietary Preferences
            </h2>
            <div className="flex-1 h-px bg-gray-200"></div>
          </div>

          {/* Primary Dietary Preference Card */}
          <Card className="p-4 border-2 border-gray-200 hover:border-toast-gold transition-all cursor-pointer mb-3">
            <button
              onClick={() => handleToggleSection('primary-diet')}
              className="w-full text-left"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-toast-gold-100 flex items-center justify-center flex-shrink-0">
                    <ICONS.food.fruit className={`${getIconSize('md')} ${getIconColor('primary')}`} />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">
                      Primary Dietary Preference
                    </h3>
                    <p className="text-xs text-gray-500">
                      {editedPrefs?.persistent?.primaryDietaryPreference || 'Not set'}
                    </p>
                  </div>
                </div>
                <ICONS.navigation.forward className={`${getIconSize('md')} ${getIconColor('muted')} transition-transform ${expandedSection === 'primary-diet' ? 'rotate-90' : ''}`} />
              </div>
            </button>

            {/* Expanded Content */}
            {expandedSection === 'primary-diet' && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="space-y-2">
                  {DIETARY_PREFERENCES.map((diet) => (
                    <button
                      key={diet.value}
                      onClick={() => {
                        if (!editedPrefs) return
                        setEditedPrefs({
                          ...editedPrefs,
                          persistent: {
                            ...editedPrefs.persistent,
                            primaryDietaryPreference: diet.value
                          }
                        })
                      }}
                      className={`w-full text-left px-3 py-2 rounded-lg border-2 transition-all ${
                        editedPrefs?.persistent?.primaryDietaryPreference === diet.value
                          ? 'border-toast-gold bg-toast-gold/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-900">{diet.label}</span>
                        {editedPrefs?.persistent?.primaryDietaryPreference === diet.value && (
                          <ICONS.actions.confirm className={`${getIconSize('sm')} text-toast-gold`} />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </Card>

          {/* Dietary Restrictions Card */}
          <Card className="p-4 border-2 border-gray-200 hover:border-toast-gold transition-all cursor-pointer">
            <button
              onClick={() => handleToggleSection('restrictions')}
              className="w-full text-left"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-toast-gold-100 flex items-center justify-center flex-shrink-0">
                    <ICONS.food.fruit className={`${getIconSize('md')} ${getIconColor('primary')}`} />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">
                      Dietary Restrictions
                    </h3>
                    <p className="text-xs text-gray-500">
                      {editedPrefs?.persistent?.dietaryRestrictions?.length || 0} restrictions
                    </p>
                  </div>
                </div>
                <ICONS.navigation.forward className={`${getIconSize('md')} ${getIconColor('muted')} transition-transform ${expandedSection === 'restrictions' ? 'rotate-90' : ''}`} />
              </div>
            </button>

            {/* Expanded Content */}
            {expandedSection === 'restrictions' && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="space-y-2 mb-4">
                  {['Gluten-Free', 'Dairy-Free', 'Nut-Free', 'Soy-Free', 'Egg-Free', 'Shellfish-Free'].map((restriction) => (
                    <label
                      key={restriction}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={editedPrefs?.persistent?.dietaryRestrictions?.includes(restriction)}
                        onChange={(e) => {
                          if (!editedPrefs) return
                          const updated = e.target.checked
                            ? [...(editedPrefs.persistent?.dietaryRestrictions || []), restriction]
                            : editedPrefs.persistent?.dietaryRestrictions?.filter(r => r !== restriction)
                          setEditedPrefs({
                            ...editedPrefs,
                            persistent: {
                              ...editedPrefs.persistent,
                              dietaryRestrictions: updated || []
                            }
                          })
                        }}
                        className="w-5 h-5 rounded border-2 border-gray-300 text-toast-gold focus:ring-2 focus:ring-toast-gold"
                      />
                      <span className="text-sm text-gray-900">{restriction}</span>
                    </label>
                  ))}
                </div>

                {/* Custom restriction input */}
                <div className="pt-3 border-t border-gray-200">
                  <p className="text-xs font-medium text-gray-700 mb-2">Add custom restriction:</p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={customRestriction}
                      onChange={(e) => setCustomRestriction(e.target.value)}
                      placeholder="e.g., No Pork, Low Sodium..."
                      className="flex-1 px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:border-toast-gold focus:ring-2 focus:ring-toast-gold/20 outline-none"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && customRestriction.trim()) {
                          e.preventDefault()
                          const trimmed = customRestriction.trim()
                          if (!editedPrefs?.persistent?.dietaryRestrictions?.includes(trimmed)) {
                            setEditedPrefs({
                              ...editedPrefs!,
                              persistent: {
                                ...editedPrefs!.persistent,
                                dietaryRestrictions: [...(editedPrefs!.persistent?.dietaryRestrictions || []), trimmed]
                              }
                            })
                          }
                          setCustomRestriction('')
                        }
                      }}
                    />
                    <button
                      onClick={() => {
                        const trimmed = customRestriction.trim()
                        if (trimmed && !editedPrefs?.persistent?.dietaryRestrictions?.includes(trimmed)) {
                          setEditedPrefs({
                            ...editedPrefs!,
                            persistent: {
                              ...editedPrefs!.persistent,
                              dietaryRestrictions: [...(editedPrefs!.persistent?.dietaryRestrictions || []), trimmed]
                            }
                          })
                          setCustomRestriction('')
                        }
                      }}
                      disabled={!customRestriction.trim()}
                      className="px-4 py-2 text-sm font-medium text-white bg-toast-gold rounded-lg hover:bg-toast-gold/90 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Add
                    </button>
                  </div>

                  {/* Show custom restrictions with remove button */}
                  {editedPrefs?.persistent?.dietaryRestrictions?.filter(r => !['Gluten-Free', 'Dairy-Free', 'Nut-Free', 'Soy-Free', 'Egg-Free', 'Shellfish-Free'].includes(r)).length > 0 && (
                    <div className="mt-3 space-y-2">
                      <p className="text-xs font-medium text-gray-700">Custom restrictions:</p>
                      {editedPrefs.persistent.dietaryRestrictions
                        .filter(r => !['Gluten-Free', 'Dairy-Free', 'Nut-Free', 'Soy-Free', 'Egg-Free', 'Shellfish-Free'].includes(r))
                        .map((restriction) => (
                          <div
                            key={restriction}
                            className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                          >
                            <span className="text-sm text-gray-900">{restriction}</span>
                            <button
                              onClick={() => {
                                const updated = editedPrefs.persistent.dietaryRestrictions?.filter(r => r !== restriction)
                                setEditedPrefs({
                                  ...editedPrefs,
                                  persistent: {
                                    ...editedPrefs.persistent,
                                    dietaryRestrictions: updated || []
                                  }
                                })
                              }}
                              className="text-gray-400 hover:text-red-600"
                            >
                              <ICONS.actions.confirm className={`${getIconSize('sm')} rotate-45`} />
                            </button>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </Card>
        </section>

        {/* APPLIANCES SECTION */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              Kitchen Appliances
            </h2>
            <div className="flex-1 h-px bg-gray-200"></div>
          </div>

          <Card className="p-4 border-2 border-gray-200 hover:border-toast-gold transition-all cursor-pointer">
            <button
              onClick={() => handleToggleSection('appliances')}
              className="w-full text-left"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-toast-gold-100 flex items-center justify-center flex-shrink-0">
                    <ICONS.food.chef className={`${getIconSize('md')} ${getIconColor('primary')}`} />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">
                      Available Appliances
                    </h3>
                    <p className="text-xs text-gray-500">
                      {editedPrefs?.persistent?.kitchenAppliances?.length || 0} appliances
                    </p>
                  </div>
                </div>
                <ICONS.navigation.forward className={`${getIconSize('md')} ${getIconColor('muted')} transition-transform ${expandedSection === 'appliances' ? 'rotate-90' : ''}`} />
              </div>
            </button>

            {/* Expanded Content */}
            {expandedSection === 'appliances' && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="space-y-2">
                  {AVAILABLE_APPLIANCES.map((appliance) => (
                    <label
                      key={appliance.value}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={editedPrefs?.persistent?.kitchenAppliances?.includes(appliance.value)}
                        onChange={(e) => {
                          if (!editedPrefs) return
                          const updated = e.target.checked
                            ? [...(editedPrefs.persistent?.kitchenAppliances || []), appliance.value]
                            : editedPrefs.persistent?.kitchenAppliances?.filter(a => a !== appliance.value)
                          setEditedPrefs({
                            ...editedPrefs,
                            persistent: {
                              ...editedPrefs.persistent,
                              kitchenAppliances: updated || []
                            }
                          })
                        }}
                        className="w-5 h-5 rounded border-2 border-gray-300 text-toast-gold focus:ring-2 focus:ring-toast-gold"
                      />
                      <span className="text-sm text-gray-900">{appliance.emoji} {appliance.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </Card>
        </section>

        {/* CUISINE PREFERENCES SECTION */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              Cuisine Preferences
            </h2>
            <div className="flex-1 h-px bg-gray-200"></div>
          </div>

          {/* Cuisine Strategy Card */}
          <Card className="p-4 border-2 border-gray-200 hover:border-toast-gold transition-all cursor-pointer">
            <button
              onClick={() => handleToggleSection('cuisine')}
              className="w-full text-left"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-toast-gold-100 flex items-center justify-center flex-shrink-0">
                    <ICONS.utility.language className={`${getIconSize('md')} ${getIconColor('primary')}`} />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">
                      Cuisine Strategy
                    </h3>
                    <p className="text-xs text-gray-500">
                      {editedPrefs?.persistent?.cuisineStrategy === 'surprise' && 'Surprise Me (AI Picks)'}
                      {editedPrefs?.persistent?.cuisineStrategy === 'mixed' && `Mixed Variety (${editedPrefs?.persistent?.favoriteCuisines?.length || 0} favorites)`}
                      {editedPrefs?.persistent?.cuisineStrategy === 'favorites-only' && `Favorites Only (${editedPrefs?.persistent?.favoriteCuisines?.length || 0} selected)`}
                      {!editedPrefs?.persistent?.cuisineStrategy && 'Not set'}
                    </p>
                  </div>
                </div>
                <ICONS.navigation.forward className={`${getIconSize('md')} ${getIconColor('muted')} transition-transform ${expandedSection === 'cuisine' ? 'rotate-90' : ''}`} />
              </div>
            </button>

            {/* Expanded Content */}
            {expandedSection === 'cuisine' && (
              <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
                {/* Surprise Me Option */}
                <button
                  onClick={() => {
                    if (!editedPrefs) return
                    setEditedPrefs({
                      ...editedPrefs,
                      persistent: {
                        ...editedPrefs.persistent,
                        cuisineStrategy: 'surprise',
                        favoriteCuisines: []
                      }
                    })
                  }}
                  className={`w-full text-left px-3 py-2 rounded-lg border-2 transition-all ${
                    editedPrefs?.persistent?.cuisineStrategy === 'surprise'
                      ? 'border-toast-gold bg-toast-gold/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-gray-900">Surprise Me!</span>
                      <p className="text-xs text-gray-500">AI picks diverse cuisines</p>
                    </div>
                    {editedPrefs?.persistent?.cuisineStrategy === 'surprise' && (
                      <ICONS.actions.confirm className={`${getIconSize('sm')} text-toast-gold`} />
                    )}
                  </div>
                </button>

                {/* Mixed Variety Option */}
                <button
                  onClick={() => {
                    if (!editedPrefs) return
                    setEditedPrefs({
                      ...editedPrefs,
                      persistent: {
                        ...editedPrefs.persistent,
                        cuisineStrategy: 'mixed',
                        favoriteCuisines: editedPrefs.persistent?.favoriteCuisines?.slice(0, 2) || []
                      }
                    })
                  }}
                  className={`w-full text-left px-3 py-2 rounded-lg border-2 transition-all ${
                    editedPrefs?.persistent?.cuisineStrategy === 'mixed'
                      ? 'border-toast-gold bg-toast-gold/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-gray-900">Mixed Variety</span>
                      <p className="text-xs text-gray-500">Pick 1-2 favorites + AI adds variety</p>
                    </div>
                    {editedPrefs?.persistent?.cuisineStrategy === 'mixed' && (
                      <ICONS.actions.confirm className={`${getIconSize('sm')} text-toast-gold`} />
                    )}
                  </div>

                  {/* Cuisine selector dropdown for mixed */}
                  {editedPrefs?.persistent?.cuisineStrategy === 'mixed' && (
                    <div className="mt-3 pt-3 border-t border-gray-200" onClick={(e) => e.stopPropagation()}>
                      <p className="text-xs text-gray-500 mb-2">
                        Select 1-2 cuisines ({editedPrefs?.persistent?.favoriteCuisines?.length || 0}/2)
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        {CUISINES.map((cuisine) => {
                          const isSelected = editedPrefs?.persistent?.favoriteCuisines?.includes(cuisine.name)
                          const maxReached = (editedPrefs?.persistent?.favoriteCuisines?.length || 0) >= 2
                          const isDisabled = !isSelected && maxReached

                          return (
                            <button
                              key={cuisine.name}
                              onClick={() => {
                                if (isDisabled || !editedPrefs) return
                                const updated = isSelected
                                  ? editedPrefs.persistent?.favoriteCuisines?.filter(c => c !== cuisine.name)
                                  : [...(editedPrefs.persistent?.favoriteCuisines || []), cuisine.name]
                                setEditedPrefs({
                                  ...editedPrefs,
                                  persistent: {
                                    ...editedPrefs.persistent,
                                    favoriteCuisines: updated || []
                                  }
                                })
                              }}
                              disabled={isDisabled}
                              className={`flex items-center gap-2 p-2 rounded-lg border transition-all text-left ${
                                isSelected
                                  ? 'border-toast-gold bg-toast-gold/5'
                                  : isDisabled
                                  ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <span className="text-sm">{cuisine.emoji}</span>
                              <span className="text-xs text-gray-900 flex-1">{cuisine.name}</span>
                              {isSelected && <ICONS.actions.confirm className={getIconSize('xs')} style={{color: '#E8A03A'}} />}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </button>

                {/* Favorites Only Option */}
                <button
                  onClick={() => {
                    if (!editedPrefs) return
                    setEditedPrefs({
                      ...editedPrefs,
                      persistent: {
                        ...editedPrefs.persistent,
                        cuisineStrategy: 'favorites-only',
                        favoriteCuisines: editedPrefs.persistent?.favoriteCuisines || []
                      }
                    })
                  }}
                  className={`w-full text-left px-3 py-2 rounded-lg border-2 transition-all ${
                    editedPrefs?.persistent?.cuisineStrategy === 'favorites-only'
                      ? 'border-toast-gold bg-toast-gold/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-gray-900">My Favorites Only</span>
                      <p className="text-xs text-gray-500">Pick 2-4 cuisines you love</p>
                    </div>
                    {editedPrefs?.persistent?.cuisineStrategy === 'favorites-only' && (
                      <ICONS.actions.confirm className={`${getIconSize('sm')} text-toast-gold`} />
                    )}
                  </div>

                  {/* Cuisine selector dropdown for favorites-only */}
                  {editedPrefs?.persistent?.cuisineStrategy === 'favorites-only' && (
                    <div className="mt-3 pt-3 border-t border-gray-200" onClick={(e) => e.stopPropagation()}>
                      <p className="text-xs text-gray-500 mb-2">
                        Select 2-4 cuisines ({editedPrefs?.persistent?.favoriteCuisines?.length || 0}/4)
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        {CUISINES.map((cuisine) => {
                          const isSelected = editedPrefs?.persistent?.favoriteCuisines?.includes(cuisine.name)
                          const maxReached = (editedPrefs?.persistent?.favoriteCuisines?.length || 0) >= 4
                          const isDisabled = !isSelected && maxReached

                          return (
                            <button
                              key={cuisine.name}
                              onClick={() => {
                                if (isDisabled || !editedPrefs) return
                                const updated = isSelected
                                  ? editedPrefs.persistent?.favoriteCuisines?.filter(c => c !== cuisine.name)
                                  : [...(editedPrefs.persistent?.favoriteCuisines || []), cuisine.name]
                                setEditedPrefs({
                                  ...editedPrefs,
                                  persistent: {
                                    ...editedPrefs.persistent,
                                    favoriteCuisines: updated || []
                                  }
                                })
                              }}
                              disabled={isDisabled}
                              className={`flex items-center gap-2 p-2 rounded-lg border transition-all text-left ${
                                isSelected
                                  ? 'border-toast-gold bg-toast-gold/5'
                                  : isDisabled
                                  ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <span className="text-sm">{cuisine.emoji}</span>
                              <span className="text-xs text-gray-900 flex-1">{cuisine.name}</span>
                              {isSelected && <ICONS.actions.confirm className={getIconSize('xs')} style={{color: '#E8A03A'}} />}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </button>
              </div>
            )}
          </Card>
        </section>

      </main>
    </div>
  )
}
